import { FC, useEffect, useMemo, useState } from "react";

import {
  IScheduleBlockEntry,
  IScheduleBlockEntryParams,
  IScheduleBlockEntryProperties,
} from "Bands/types";
import { useBandRegionalSettings, useIsStartDateBeforeEndDate } from "Bands/utils";
import { FormBuilder } from "shared/components";
import { API_ENDPOINTS } from "shared/constants";
import { IAPIError, IAPIState, IFormButtons } from "shared/types";
import { createNewDate, getRegionalSettingsOptions, isoToUTCString } from "shared/utils";

import { getFormFields } from "./formData";
import { omit } from "lodash-es";

interface ScheduleBlockEntryFormProps {
  apiStatus?: IAPIState;
  error?: boolean | string;
  defaultValues?: IScheduleBlockEntryProperties;
  lastStartEndDate?: Pick<IScheduleBlockEntryProperties, "startDate" | "endDate">;
  buttons?: IFormButtons;
  resetForm?: boolean;
  apiError?: IAPIError | null;
  excludeUsers?: string[] | undefined;
  handleSubmit: (data: {
    entry: IScheduleBlockEntryParams;
    startDate?: string;
    endDate?: string;
  }) => void;
  showEditEntryModal?: IScheduleBlockEntry | null;
  isEdit?: boolean;
}

export const ScheduleBlockEntryForm: FC<ScheduleBlockEntryFormProps> = ({
  apiStatus,
  error,
  buttons,
  handleSubmit,
  defaultValues,
  lastStartEndDate,
  resetForm,
  apiError,
  isEdit,
  showEditEntryModal,
  excludeUsers = [],
}) => {
  const { start, end, isStartBeforeEnd, handleStartDateChange, handleEndDateChange } =
    useIsStartDateBeforeEndDate({
      includeEqual: true,
      defaultValues: { ...defaultValues },
    });

  const [forceUpdateValues, setForceUpdateValues] = useState<
    IScheduleBlockEntryProperties | undefined
  >();

  // if start date changes, update end date to the future
  useEffect(() => {
    if (start?.date && end?.date && !isEdit) {
      const startDateCompare = new Date(start?.date);
      const endDateCompare = new Date(end?.date);

      if (startDateCompare > endDateCompare) {
        const forcedEndDate = new Date(start.date);
        forcedEndDate.setDate(forcedEndDate.getDate());

        setForceUpdateValues({
          endDate: forcedEndDate.toISOString().split("T")[0],
        });

        handleEndDateChange({
          ...end,
          date: forcedEndDate.toISOString().split("T")[0],
        });
      }
    }
  }, [start?.date, end?.date, handleEndDateChange, end, isEdit]);

  // if last used start/end date are passed, use them
  useEffect(() => {
    if (lastStartEndDate?.startDate && lastStartEndDate?.endDate) {
      setForceUpdateValues({
        startDate: lastStartEndDate?.endDate,
        endDate: lastStartEndDate?.endDate,
      });
    }
  }, [lastStartEndDate]);

  useEffect(() => {
    if (apiStatus === IAPIState.succeeded) {
      setForceUpdateValues({
        startTime: "",
        endTime: "",
      });
    }
  }, [apiStatus]);

  const onSubmit = (entry: IScheduleBlockEntryProperties) => {
    const { startDate, startTimezone, endDate, endTimezone, users, ...rest } = entry;

    const { timezoneToUtc } = getRegionalSettingsOptions();

    const parsedStart = createNewDate(`${startDate} ${start?.time}`.replace(/-/g, "/"));

    const parsedEnd = createNewDate(`${endDate} ${end?.time}`.replace(/-/g, "/"));

    const cleanEntry = {
      ...rest,
      users: users?.map(({ value }) => API_ENDPOINTS.BAND_USER(value)),
      start:
        startDate && startTimezone
          ? isoToUTCString({
              iso: startDate,
              hour: start?.time,
              timeZone: timezoneToUtc(startTimezone, parsedStart.toString()),
            })
          : undefined,
      startTimezone,
      end:
        endDate && endTimezone
          ? isoToUTCString({
              iso: endDate,
              hour: end?.time,
              timeZone: timezoneToUtc(endTimezone, parsedEnd.toString()),
            })
          : undefined,
      endTimezone,
    } as IScheduleBlockEntryParams;

    handleSubmit({
      entry: omit(cleanEntry, ["startTime", "endTime"]),
      startDate,
      endDate,
    });
  };

  const { dateFormat, timeFormat } = useBandRegionalSettings();
  const fields = useMemo(
    () =>
      getFormFields({
        excludeUsers,
        handleStartDateChange,
        handleEndDateChange,
        isStartBeforeEnd,
        dateFormat,
        timeFormat,
        isEdit,
        showEditEntryModal,
      }),
    [
      excludeUsers,
      handleStartDateChange,
      handleEndDateChange,
      isStartBeforeEnd,
      dateFormat,
      timeFormat,
      isEdit,
      showEditEntryModal,
    ]
  );

  return (
    <FormBuilder
      isSubmitting={apiStatus === IAPIState.pending}
      apiErrorMessage={error}
      fields={fields}
      onSubmit={onSubmit}
      buttons={buttons}
      defaultValues={defaultValues}
      forceUpdateValues={forceUpdateValues}
      resetFormOnSubmit={true}
      resetForm={resetForm}
      apiError={apiError}
      mode="onSubmit"
      reValidateMode="onSubmit"
    />
  );
};
